import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { DEFAULT_RULE_CONFIG, getViolationsByEvent, runLinting } from '../core/linter.ts';
import { EventItem, EventTemplate, RuleConfig } from '../core/types.ts';
import { exportEventsJSON, loadEvents, loadRuleConfig, loadTemplates, parseImportJSON, saveEvents, saveRuleConfig, setAuthToken } from '../services/storage.ts';
import { listenToUrlChanges, parseUrlState, updateUrlState } from '../services/urlState.ts';
import { useRealtimeSync } from '../services/realtime.ts';
import { RealtimeToasts } from './components/RealtimeToasts.tsx';
import { Navbar, NavTabId } from './components/Navbar.tsx';
import { EventModal } from './components/EventModal.tsx';
import { TemplateModal } from './components/TemplateModal.tsx';
import { ConfirmDeleteModal } from './components/ConfirmDeleteModal.tsx';
import { AuthLockModal } from './components/AuthLockModal.tsx';
import { CalendarPage } from './pages/CalendarPage.tsx';
import { AnalysisPage } from './pages/AnalysisPage.tsx';
import { FinancesPage } from './pages/FinancesPage.tsx';
import './styles.css';

const App: React.FC = () => {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [templates, setTemplates] = useState<EventTemplate[]>([]);
  const [activeTab, setActiveTab] = useState<NavTabId>(() => parseUrlState().tab);
  const [ruleConfig, setRuleConfig] = useState<RuleConfig>(() => loadRuleConfig());

  const handleUpdateRuleConfig = (newConfig: RuleConfig) => {
    setRuleConfig(newConfig);
    saveRuleConfig(newConfig);
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [eventToEdit, setEventToEdit] = useState<EventItem | null>(null);
  const [eventToDelete, setEventToDelete] = useState<EventItem | null>(null);

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState('');

  const {
    currentUser,
    connectedUsers,
    isConnected,
    toasts,
    dismissToast,
    updateUserName,
    broadcastEventsChange,
  } = useRealtimeSync(events, setEvents, setTemplates, activeTab === 'calendar' ? 'calendar' : 'analysis');

  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('esn_theme') as 'dark' | 'light') || 'dark';
  });

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('esn_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
  };

  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadInitialData = () => {
    setIsAuthModalOpen(false);
    setAuthErrorMessage('');

    Promise.all([
      loadEvents().catch((err) => {
        if (err?.message === 'UNAUTHORIZED') throw err;
        return [];
      }),
      loadTemplates().catch((err) => {
        if (err?.message === 'UNAUTHORIZED') throw err;
        return [];
      }),
    ])
      .then(([data, tmpls]) => {
        setEvents(data);
        setTemplates(tmpls);
      })
      .catch((err) => {
        if (err?.message === 'UNAUTHORIZED') {
          setIsAuthModalOpen(true);
          setAuthErrorMessage('Invalid or expired access key. Please enter a valid key.');
        }
      });
  };

  // Load events and templates on mount & strip URL token
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('key') || params.get('token');
    if (urlToken) {
      setAuthToken(urlToken);
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, document.title, cleanUrl);
    }
    loadInitialData();
  }, []);

  const handleTabChange = (newTab: NavTabId) => {
    setActiveTab(newTab);
    updateUrlState({ tab: newTab });
  };

  // Synchronize URL state when events load or deep-linked edit URL is present
  useEffect(() => {
    if (events.length === 0) return;
    const initialUrl = parseUrlState();
    if (initialUrl.editEventId) {
      const target = events.find(e => e.id === initialUrl.editEventId);
      if (target) {
        setEventToEdit(target);
        setIsModalOpen(true);
      }
    }
  }, [events]);

  // Listen to browser Back/Forward navigation (popstate)
  useEffect(() => {
    return listenToUrlChanges((urlState) => {
      if (urlState.tab && urlState.tab !== activeTab) {
        setActiveTab(urlState.tab);
      }
      if (urlState.editEventId) {
        const target = events.find(e => e.id === urlState.editEventId);
        if (target) {
          setEventToEdit(target);
          setIsModalOpen(true);
        }
      } else {
        setIsModalOpen(false);
        setEventToEdit(null);
      }
    });
  }, [events, activeTab]);

  const violations = runLinting(events, ruleConfig);
  const violationsMap = getViolationsByEvent(violations);

  const hardViolationsCount = violations.filter(v => v.severity === 'hard').length;
  const softViolationsCount = violations.filter(v => v.severity === 'soft').length;

  const handleEventsChange = async (newEvents: EventItem[], actionDetail?: string) => {
    setEvents(newEvents);
    await saveEvents(newEvents);
    broadcastEventsChange(newEvents, actionDetail);
  };

  const handleAddEvent = () => {
    setEventToEdit(null);
    setIsModalOpen(true);
    updateUrlState({ editEventId: undefined });
  };

  const handleOpenTemplates = () => {
    setIsTemplateModalOpen(true);
  };

  const handleSelectTemplate = (tmpl: EventTemplate) => {
    setIsTemplateModalOpen(false);

    const startTimeStr = tmpl.defaultStartTime || '10:00';
    const durationHours = tmpl.defaultDurationHours || 2;

    const startDate = new Date(`2026-09-28T${startTimeStr}`);
    const endDate = new Date(startDate.getTime() + durationHours * 3600 * 1000);

    const formatISO = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    };

    const draftEvent: EventItem = {
      id: `ev-${Date.now()}`,
      title: tmpl.title,
      start: formatISO(startDate),
      end: formatISO(endDate),
      location: tmpl.location || 'TBD',
      price: tmpl.price !== undefined ? tmpl.price : 0,
      fixedCost: tmpl.fixedCost !== undefined ? tmpl.fixedCost : 0,
      costPerPerson: tmpl.costPerPerson !== undefined ? tmpl.costPerPerson : 0,
      capacity: tmpl.capacity !== undefined ? tmpl.capacity : 100,
      organisers: tmpl.organisers !== undefined ? tmpl.organisers : 3,
      category: tmpl.category || 'Social',
      description: tmpl.description || '',
    };

    setEventToEdit(draftEvent);
    setIsModalOpen(true);
    updateUrlState({ editEventId: draftEvent.id });
  };

  const handleEditEvent = (event: EventItem) => {
    setEventToEdit(event);
    setIsModalOpen(true);
    updateUrlState({ editEventId: event.id });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEventToEdit(null);
    updateUrlState({ editEventId: undefined });
  };

  const handleDeleteEvent = (id: string) => {
    const target = events.find(e => e.id === id);
    if (target) {
      setEventToDelete(target);
    }
  };

  const handleConfirmDelete = async () => {
    if (!eventToDelete) return;
    const title = eventToDelete.title;
    const updated = events.filter(e => e.id !== eventToDelete.id);
    await handleEventsChange(updated, `deleted event "${title}"`);
    setEventToDelete(null);
    if (eventToEdit?.id === eventToDelete.id) {
      handleCloseModal();
    }
  };

  const handleDuplicateEvent = async (eventToDuplicate: EventItem) => {
    setIsModalOpen(false);
    setEventToEdit(null);

    const duplicate: EventItem = {
      ...eventToDuplicate,
      id: eventToDuplicate.id && !events.some(e => e.id === eventToDuplicate.id)
        ? eventToDuplicate.id
        : `ev-${Date.now()}`,
      title: eventToDuplicate.title.endsWith('(Copy)')
        ? eventToDuplicate.title
        : `${eventToDuplicate.title} (Copy)`,
    };

    const updated = [...events, duplicate];
    await handleEventsChange(updated, `duplicated "${eventToDuplicate.title}"`);

    // Open popup with the new duplicated event
    setEventToEdit(duplicate);
    setIsModalOpen(true);
    updateUrlState({ editEventId: duplicate.id });
  };

  const handleSaveModalEvent = async (eventData: EventItem) => {
    const existingIndex = events.findIndex(e => e.id === eventData.id);
    let updated: EventItem[];
    let actionDesc = `added "${eventData.title}"`;
    if (existingIndex >= 0) {
      updated = events.map(e => (e.id === eventData.id ? eventData : e));
      actionDesc = `updated "${eventData.title}"`;
    } else {
      updated = [...events, eventData];
    }
    await handleEventsChange(updated, actionDesc);
    handleCloseModal();
  };

  const handleRescheduleEvent = async (eventId: string, newDateStr: string) => {
    const ev = events.find(e => e.id === eventId);
    if (!ev) return;

    const oldStart = new Date(ev.start);
    const oldEnd = new Date(ev.end);
    const durationMs = oldEnd.getTime() - oldStart.getTime();

    const [targetYear, targetMonth, targetDay] = newDateStr.split('-').map(Number);

    const newStart = new Date(oldStart);
    newStart.setFullYear(targetYear, targetMonth - 1, targetDay);

    const newEnd = new Date(newStart.getTime() + durationMs);

    const formatISO = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    };

    const updatedEvent: EventItem = {
      ...ev,
      start: formatISO(newStart),
      end: formatISO(newEnd),
    };

    const updatedEvents = events.map(e => (e.id === eventId ? updatedEvent : e));
    await handleEventsChange(updatedEvents, `rescheduled "${ev.title}"`);
  };

  const handleExportJSON = () => {
    exportEventsJSON(events);
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const imported = await parseImportJSON(file);
      await handleEventsChange(imported, `imported ${imported.length} events`);
      alert(`Successfully imported ${imported.length} events from JSON!`);
    } catch (err: any) {
      alert(`Import error: ${err.message}`);
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div>
      <Navbar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onAddEvent={handleAddEvent}
        onOpenTemplates={handleOpenTemplates}
        onExportJSON={handleExportJSON}
        onImportClick={handleImportClick}
        hardViolationsCount={hardViolationsCount}
        softViolationsCount={softViolationsCount}
        theme={theme}
        onToggleTheme={toggleTheme}
        currentUser={currentUser}
        connectedUsers={connectedUsers}
        isConnected={isConnected}
        onUpdateName={updateUserName}
      />

      <RealtimeToasts toasts={toasts} onDismiss={dismissToast} />

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileImport}
        accept=".json,application/json"
        style={{ display: 'none' }}
      />

      {activeTab === 'calendar' && (
        <CalendarPage
          events={events}
          violationsMap={violationsMap}
          onAddEvent={handleAddEvent}
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onRescheduleEvent={handleRescheduleEvent}
        />
      )}

      {activeTab === 'analysis' && (
        <AnalysisPage
          events={events}
          violations={violations}
          ruleConfig={ruleConfig}
          onUpdateRuleConfig={handleUpdateRuleConfig}
          onEditEvent={handleEditEvent}
          onExportJSON={handleExportJSON}
          onImportClick={handleImportClick}
        />
      )}

      {activeTab === 'finances' && (
        <FinancesPage
          events={events}
          ruleConfig={ruleConfig}
          onEditEvent={handleEditEvent}
          onExportJSON={handleExportJSON}
          onImportClick={handleImportClick}
        />
      )}

      <EventModal
        isOpen={isModalOpen}
        eventToEdit={eventToEdit}
        onClose={handleCloseModal}
        onSave={handleSaveModalEvent}
        onDelete={handleDeleteEvent}
        onDuplicate={handleDuplicateEvent}
      />

      <TemplateModal
        isOpen={isTemplateModalOpen}
        templates={templates}
        onClose={() => setIsTemplateModalOpen(false)}
        onSelectTemplate={handleSelectTemplate}
      />

      <ConfirmDeleteModal
        isOpen={!!eventToDelete}
        event={eventToDelete}
        onClose={() => setEventToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <AuthLockModal
        isOpen={isAuthModalOpen}
        onAuthenticated={loadInitialData}
        errorMessage={authErrorMessage}
      />
    </div>
  );
};

const container = document.getElementById('root');
if (container) {
  const root = createRoot(container);
  root.render(<App />);
}
